import { useCallback, useEffect } from 'react'
import { AppConfig, AppConfigActions } from '@AppState/app-config'
import { useAppDispatch } from '@AppState/redux-store'
import { CONFIG_PARAMS } from './types'
import { useSearchParams, useLocation } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import { Utils } from '../../utilities/utils'
import { LOCAL_HOST_CONFIG } from '@API/config/local-host-config'
import { ConfigUtils, hostConfigByName } from '../../config/utils'
import { HOST_NAMES } from '../../config/types'

export const AppConfigLoader = () => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const { pathname } = useLocation()

  const hostName = searchParams.get(CONFIG_PARAMS.HOST_NAME)

  if (!hostName) {
    throw Error('Host name not found')
  }

  const getCookieName = useCallback((prefix: string, key: CONFIG_PARAMS) => {
    return `${prefix}.${key}`
  }, [])

  const cookieNames = Object.values(CONFIG_PARAMS).map((param) => getCookieName(hostName, param))
  const [cookies] = useCookies([...cookieNames])

  const isMaintenanceEnabled = useCallback(() => {
    return pathname === '/maintenance'
  }, [pathname])

  const getConfigValue = useCallback(
    function checkUrlThenCookie(paramName: CONFIG_PARAMS, canBeNull: boolean = false) {
      if (searchParams.get(paramName)) {
        console.log(`🔵 Using Search Param for: ${paramName}`)
        return searchParams.get(paramName)
      }

      const cookieName = getCookieName(hostName, paramName)
      if (cookies[cookieName]) {
        console.log(`🍪 Using Cookie Param for: ${cookieName}`)
        return cookies[cookieName]
      }

      console.log(`🔴 Value not found for: ${paramName}`)
      if (Utils.isLocalhost()) {
        const retVal = LOCAL_HOST_CONFIG[hostName][paramName]
        console.log(`🖥️ Using default ${paramName} value: ${retVal}`)
        return retVal
      }

      if (!isMaintenanceEnabled() && !canBeNull) {
        throw Error(`${paramName} not found`)
      }
    },
    [cookies, getCookieName, hostName, searchParams, isMaintenanceEnabled],
  )

  useEffect(() => {
    const hostConfig = hostConfigByName[hostName as HOST_NAMES]

    if (!hostConfig) {
      return
    }

    const properties = ConfigUtils.brandingCustomProperties(hostConfig.branding)

    for (const [property, value] of Object.entries(properties)) {
      document.documentElement.style.setProperty(property, value)
    }
  }, [hostName])

  useEffect(() => {
    console.log(`💠 Loading App Config`)

    const appConfig: AppConfig = {
      gameDataServiceUrl: getConfigValue(CONFIG_PARAMS.GAME_DATA_SERVICE_URL),
      gameDataApiKey: getConfigValue(CONFIG_PARAMS.GAME_DATA_API_KEY),
      hostName: getConfigValue(CONFIG_PARAMS.HOST_NAME),
      playerDataServiceUrl: getConfigValue(CONFIG_PARAMS.PLAYER_DATA_SERVICE_URL),
      playerDataApiKey: getConfigValue(CONFIG_PARAMS.PLAYER_DATA_API_KEY),
      enableClaimForm: getConfigValue(CONFIG_PARAMS.ENABLE_CLAIM_FORM, true) === 'true',
    }

    dispatch(AppConfigActions.setAppConfig(appConfig))
  }, [dispatch, getConfigValue])

  return <></>
}
